import { useRef, useState, useEffect } from 'react';
import { Button, Input, Nav, NavItem, Progress, Spinner } from 'reactstrap';
import {
    BiCheck, BiChevronDown, BiChevronUp, BiEnvelope,
    BiHide, BiLock, BiPlus, BiShow, BiTrash, BiUpload,
    BiUser,
} from 'react-icons/bi';
import { useOrganization, useUser } from '@clerk/clerk-react';
import useIsOrgAdmin from '../hooks/useIsOrgAdmin';
import useTierFeatures from '../hooks/useTierFeatures';
import useCompanyBranding from '../hooks/useCompanyBranding';
import { useUpdateBranding } from '../hooks/useUpdateBranding';
import useAdminCategories from '../hooks/useAdminCategories';
import {
    useCreateCategory,
    useReorderCategories,
    useToggleCategoryActive,
    useUpdateCategory,
} from '../hooks/useCategoryMutations';
import { CATEGORY_TARGET } from '../hooks/useCategories';
import useBillingUsage from '../hooks/useBillingUsage';
import { createPortalSession, cancelPendingTierChange } from '../api/billingApi';
import { checkInviteEligibility, inviteMember } from '../api/membersApi';
import ChangePlanModal from '../components/billing/ChangePlanModal';
import IntegrationsSection from '../components/integrations/IntegrationsSection';
import DialogBox from '../components/utility/dialogBox';
import { toast } from 'react-toastify';
import { apiFetch } from '../services/apiFetch';
const API_URL = import.meta.env.VITE_API_URL;
import { syncLogo, syncName, fetchBaseCurrency, updateBaseCurrency } from '../api/settingsApi';
import { CURRENCIES, DEFAULT_CURRENCY } from '../constants/currencies';
import useAdminCostTypes from '../hooks/useAdminCostTypes';
import {
    useCreateCostType,
    useUpdateCostType,
    useToggleCostTypeActive,
    useReorderCostTypes,
} from '../hooks/useCostTypeMutations';
import useAdminJobTypes from '../hooks/useAdminJobTypes';
import {
    useCreateJobType,
    useUpdateJobType,
    useToggleJobTypeActive,
    useReorderJobTypes,
} from '../hooks/useJobTypeMutations';

// ── Template thumbnail SVGs ───────────────────────────────────────────────────
// Each thumbnail is an inline SVG schematic (~90×120, A4 proportions) that
// uses the brand colour. They update live when the user changes their colour.

function TemplateThumbnailClassic({ colour }) {
    return (
        <svg viewBox="0 0 90 120" xmlns="http://www.w3.org/2000/svg" width="90" height="120">
            {/* Header: logo block left */}
            <rect x="6" y="8" width="28" height="14" rx="2" fill={colour} opacity="0.85" />
            {/* "Quote" label */}
            <rect x="6" y="25" width="16" height="3" rx="1" fill="#94a3b8" />
            {/* Right meta lines */}
            <rect x="58" y="10" width="26" height="3" rx="1" fill="#94a3b8" />
            <rect x="60" y="15" width="24" height="2" rx="1" fill="#cbd5e1" />
            <rect x="60" y="19" width="24" height="2" rx="1" fill="#cbd5e1" />
            {/* Accent rule */}
            <rect x="6" y="32" width="78" height="1.5" rx="0.5" fill={colour} />
            {/* Customer block */}
            <rect x="6" y="38" width="14" height="2" rx="1" fill="#94a3b8" />
            <rect x="6" y="42" width="26" height="3" rx="1" fill="#334155" />
            {/* Table header */}
            <rect x="6" y="52" width="78" height="7" rx="1" fill="none" stroke={colour} strokeWidth="0.8" />
            <rect x="6" y="52" width="36" height="7" rx="0" fill="none" />
            {/* Col dividers */}
            <line x1="42" y1="52" x2="42" y2="59" stroke={colour} strokeWidth="0.5" />
            <line x1="60" y1="52" x2="60" y2="59" stroke={colour} strokeWidth="0.5" />
            {/* Header text lines */}
            <rect x="8"  y="54.5" width="14" height="2" rx="0.5" fill={colour} opacity="0.7" />
            <rect x="70" y="54.5" width="12" height="2" rx="0.5" fill={colour} opacity="0.7" />
            {/* Data rows */}
            {[0,1,2,3].map(i => (
                <g key={i}>
                    <rect x="6"  y={62+i*7} width="32" height="2" rx="0.5" fill="#cbd5e1" />
                    <rect x="70" y={62+i*7} width="12" height="2" rx="0.5" fill="#cbd5e1" />
                    <line x1="6" y1={66+i*7} x2="84" y2={66+i*7} stroke="#e2e8f0" strokeWidth="0.5" />
                </g>
            ))}
            {/* Totals */}
            <rect x="52" y="94" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="99" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="104" width="32" height="3" rx="0.5" fill={colour} opacity="0.8" />
        </svg>
    );
}

function TemplateThumbnailModern({ colour }) {
    return (
        <svg viewBox="0 0 90 120" xmlns="http://www.w3.org/2000/svg" width="90" height="120">
            {/* Company name left */}
            <rect x="6" y="8" width="20" height="2.5" rx="0.5" fill="#334155" />
            {/* Logo right */}
            <rect x="58" y="5" width="26" height="14" rx="2" fill={colour} opacity="0.85" />
            {/* "QUOTE" large */}
            <rect x="42" y="22" width="42" height="6" rx="1" fill={colour} opacity="0.9" />
            {/* Accent rule */}
            <rect x="6" y="31" width="78" height="1" rx="0.5" fill={colour} />
            {/* Customer + meta */}
            <rect x="6" y="37" width="14" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="6" y="41" width="28" height="2.5" rx="0.5" fill="#334155" />
            <rect x="55" y="37" width="29" height="2" rx="0.5" fill="#cbd5e1" />
            <rect x="55" y="41" width="29" height="2" rx="0.5" fill="#cbd5e1" />
            {/* Full-colour table header */}
            <rect x="6" y="50" width="78" height="7" rx="1" fill={colour} />
            <rect x="8"  y="52.5" width="16" height="2" rx="0.5" fill="white" opacity="0.9" />
            <rect x="70" y="52.5" width="12" height="2" rx="0.5" fill="white" opacity="0.9" />
            {/* Data rows */}
            {[0,1,2,3].map(i => (
                <g key={i}>
                    <rect x="6"  y={60+i*7} width="34" height="2" rx="0.5" fill="#cbd5e1" />
                    <rect x="70" y={60+i*7} width="12" height="2" rx="0.5" fill="#cbd5e1" />
                    <line x1="6" y1={64+i*7} x2="84" y2={64+i*7} stroke="#e2e8f0" strokeWidth="0.5" />
                </g>
            ))}
            {/* Totals */}
            <rect x="52" y="92" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="97" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="102" width="32" height="3" rx="0.5" fill="#334155" opacity="0.8" />
        </svg>
    );
}

function TemplateThumbnailCorporate({ colour }) {
    return (
        <svg viewBox="0 0 90 120" xmlns="http://www.w3.org/2000/svg" width="90" height="120">
            {/* Logo left */}
            <rect x="6" y="6" width="24" height="14" rx="2" fill={colour} opacity="0.85" />
            {/* "Quote" + meta right */}
            <rect x="58" y="8" width="26" height="5" rx="1" fill={colour} opacity="0.7" />
            <rect x="58" y="15" width="26" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="58" y="19" width="26" height="2" rx="0.5" fill="#cbd5e1" />
            {/* Rule */}
            <rect x="6" y="24" width="78" height="0.8" rx="0.4" fill="#e2e8f0" />
            {/* Bordered customer box */}
            <rect x="6" y="28" width="48" height="16" rx="1" fill="none" stroke="#e2e8f0" strokeWidth="1" />
            <rect x="9" y="31" width="10" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="9" y="35" width="24" height="2.5" rx="0.5" fill="#334155" />
            <rect x="9" y="39" width="18" height="2" rx="0.5" fill="#cbd5e1" />
            {/* Full-colour table header */}
            <rect x="6" y="50" width="78" height="7" rx="1" fill={colour} />
            <rect x="8"  y="52.5" width="16" height="2" rx="0.5" fill="white" opacity="0.9" />
            <rect x="70" y="52.5" width="12" height="2" rx="0.5" fill="white" opacity="0.9" />
            {/* Data rows */}
            {[0,1,2,3].map(i => (
                <g key={i}>
                    <rect x="6"  y={60+i*7} width="34" height="2" rx="0.5" fill="#cbd5e1" />
                    <rect x="70" y={60+i*7} width="12" height="2" rx="0.5" fill="#cbd5e1" />
                    <line x1="6" y1={64+i*7} x2="84" y2={64+i*7} stroke="#e2e8f0" strokeWidth="0.5" />
                </g>
            ))}
            {/* Totals */}
            <rect x="52" y="92" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="97" width="32" height="3" rx="0.5" fill={colour} opacity="0.8" />
        </svg>
    );
}

function TemplateThumbnailFormal({ colour }) {
    return (
        <svg viewBox="0 0 90 120" xmlns="http://www.w3.org/2000/svg" width="90" height="120">
            {/* Logo top left */}
            <rect x="6" y="5" width="22" height="12" rx="2" fill={colour} opacity="0.85" />
            {/* "QUOTATION" centred */}
            <rect x="18" y="21" width="54" height="5" rx="1" fill={colour} opacity="0.8" />
            {/* Accent rule */}
            <rect x="6" y="29" width="78" height="1.5" rx="0.5" fill={colour} />
            {/* Two-col: Bill To left, meta right */}
            <rect x="6" y="34" width="10" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="6" y="38" width="26" height="2.5" rx="0.5" fill="#334155" />
            <rect x="52" y="34" width="22" height="2" rx="0.5" fill="#cbd5e1" />
            <rect x="52" y="38" width="32" height="2" rx="0.5" fill="#cbd5e1" />
            <rect x="52" y="42" width="28" height="2" rx="0.5" fill="#cbd5e1" />
            {/* Full-colour table header */}
            <rect x="6" y="50" width="78" height="7" rx="1" fill={colour} />
            <rect x="8"  y="52.5" width="16" height="2" rx="0.5" fill="white" opacity="0.9" />
            <rect x="70" y="52.5" width="12" height="2" rx="0.5" fill="white" opacity="0.9" />
            {/* Data rows */}
            {[0,1,2,3].map(i => (
                <g key={i}>
                    <rect x="6"  y={60+i*7} width="34" height="2" rx="0.5" fill="#cbd5e1" />
                    <rect x="70" y={60+i*7} width="12" height="2" rx="0.5" fill="#cbd5e1" />
                    <line x1="6" y1={64+i*7} x2="84" y2={64+i*7} stroke="#e2e8f0" strokeWidth="0.5" />
                </g>
            ))}
            {/* Totals */}
            <rect x="52" y="92" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="97" width="32" height="3" rx="0.5" fill={colour} opacity="0.8" />
            {/* Signature line */}
            <line x1="6" y1="110" x2="38" y2="110" stroke="#cbd5e1" strokeWidth="0.8" />
            <line x1="50" y1="110" x2="82" y2="110" stroke="#cbd5e1" strokeWidth="0.8" />
        </svg>
    );
}

function TemplateThumbnailTrade({ colour }) {
    return (
        <svg viewBox="0 0 90 120" xmlns="http://www.w3.org/2000/svg" width="90" height="120">
            {/* Company name left */}
            <rect x="6" y="7" width="28" height="4" rx="1" fill="#334155" />
            <rect x="6" y="13" width="18" height="2.5" rx="0.5" fill="#94a3b8" />
            {/* Logo right */}
            <rect x="56" y="5" width="28" height="16" rx="2" fill={colour} opacity="0.85" />
            {/* Rule */}
            <rect x="6" y="24" width="78" height="0.8" rx="0.4" fill="#e2e8f0" />
            {/* "To:" block */}
            <rect x="6" y="29" width="8" height="2.5" rx="0.5" fill="#334155" />
            <rect x="6" y="33" width="28" height="2.5" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="29" width="32" height="2" rx="0.5" fill="#cbd5e1" />
            <rect x="52" y="33" width="26" height="2" rx="0.5" fill="#cbd5e1" />
            {/* Minimal table header */}
            <rect x="6" y="41" width="36" height="2.5" rx="0.5" fill="#94a3b8" />
            <rect x="70" y="41" width="14" height="2.5" rx="0.5" fill="#94a3b8" />
            <line x1="6" y1="45" x2="84" y2="45" stroke="#cbd5e1" strokeWidth="0.6" />
            {/* Data rows */}
            {[0,1,2,3].map(i => (
                <g key={i}>
                    <rect x="6"  y={48+i*7} width="32" height="2" rx="0.5" fill="#cbd5e1" />
                    <rect x="70" y={48+i*7} width="12" height="2" rx="0.5" fill="#cbd5e1" />
                    <line x1="6" y1={52+i*7} x2="84" y2={52+i*7} stroke="#e2e8f0" strokeWidth="0.5" />
                </g>
            ))}
            {/* Subtotal rows */}
            <rect x="52" y="82" width="32" height="2" rx="0.5" fill="#94a3b8" />
            <rect x="52" y="87" width="32" height="2" rx="0.5" fill="#94a3b8" />
            {/* Highlighted total footer */}
            <rect x="6" y="93" width="78" height="8" rx="1" fill={colour} />
            <rect x="9" y="95.5" width="18" height="3" rx="0.5" fill="white" opacity="0.9" />
            <rect x="62" y="95.5" width="18" height="3" rx="0.5" fill="white" opacity="0.9" />
        </svg>
    );
}

const TEMPLATES = [
    { id: 'classic',   label: 'Classic',    Thumb: TemplateThumbnailClassic,
      description: 'Clean and minimal. Subtle accent line, standard table.' },
    { id: 'modern',    label: 'Modern',     Thumb: TemplateThumbnailModern,
      description: 'Bold "QUOTE" heading. Full-colour table header.' },
    { id: 'corporate', label: 'Corporate',  Thumb: TemplateThumbnailCorporate,
      description: 'Structured two-column header. Customer in a bordered box.' },
    { id: 'formal',    label: 'Formal',     Thumb: TemplateThumbnailFormal,
      description: '"QUOTATION" title centred. Two-column layout. Signature line.' },
    { id: 'trade',     label: 'Trade',      Thumb: TemplateThumbnailTrade,
      description: 'Logo right, company name left. Accent-coloured total footer.' },
];

// ── Branding section ──────────────────────────────────────────────────────────

const DEBOUNCE_MS = 750;

function BrandingSection() {
    const { data: branding, isLoading, error } = useCompanyBranding();
    const updateBranding = useUpdateBranding();

    const saveTimerRef   = useRef(null);

    const [colourValue,    setColourValue]    = useState(null);
    const [contact,        setContact]        = useState(null);
    const [includeSignature, setIncludeSignature] = useState(null);
    const [templateSaving, setTemplateSaving] = useState(false);
    const [autoSaveState,  setAutoSaveState]  = useState('idle'); // idle | saving | saved

    if (isLoading) return <div className="d-flex justify-content-center py-4"><Spinner /></div>;
    if (error) return <div className="text-danger">Failed to load branding settings.</div>;

    const currentColour    = colourValue     ?? branding.brandColour    ?? '#26ada9';
    const selectedTemplate = branding.quoteTemplate ?? 'classic';
    const currentSignature = includeSignature ?? branding.includeSignature ?? false;
    const currentContact   = contact ?? {
        addressLine1: branding.addressLine1 ?? '',
        addressLine2: branding.addressLine2 ?? '',
        townCity:     branding.townCity     ?? '',
        postcode:     branding.postcode     ?? '',
        phone:        branding.phone        ?? '',
        email:        branding.email        ?? '',
        website:      branding.website      ?? '',
    };

    // Schedules a debounced save with the fully-merged payload.
    // Colour and contact each call this so neither accidentally overwrites the other.
    function scheduleSave(payload) {
        clearTimeout(saveTimerRef.current);
        setAutoSaveState('idle');
        saveTimerRef.current = setTimeout(async () => {
            setAutoSaveState('saving');
            try {
                await updateBranding.mutateAsync(payload);
                setAutoSaveState('saved');
                setTimeout(() => setAutoSaveState('idle'), 2000);
            } catch {
                setAutoSaveState('idle');
            }
        }, DEBOUNCE_MS);
    }

    function handleColourChange(e) {
        const val = e.target.value;
        setColourValue(val);
        scheduleSave({
            brandColour: val, quoteTemplate: selectedTemplate,
            includeSignature: currentSignature, ...currentContact,
        });
    }

    function handleContactChange(field, val) {
        const next = { ...currentContact, [field]: val };
        setContact(next);
        scheduleSave({
            brandColour: currentColour, quoteTemplate: selectedTemplate,
            includeSignature: currentSignature, ...next,
        });
    }

    async function handleSelectTemplate(id) {
        setTemplateSaving(true);
        try {
            await updateBranding.mutateAsync({
                brandColour: branding.brandColour, quoteTemplate: id,
                includeSignature: currentSignature, ...currentContact,
            });
        } finally {
            setTemplateSaving(false);
        }
    }

    async function handleToggleSignature(val) {
        setIncludeSignature(val);
        await updateBranding.mutateAsync({
            brandColour: currentColour, quoteTemplate: selectedTemplate,
            includeSignature: val, ...currentContact,
        });
    }

    // Small inline save indicator for debounced fields
    const SaveIndicator = () => {
        if (autoSaveState === 'saving') return <span className="text-muted small ms-2"><Spinner size="sm" className="me-1" />Saving…</span>;
        if (autoSaveState === 'saved')  return <span className="text-success small ms-2"><BiCheck /> Saved</span>;
        return null;
    };

    return (
        <div className="surface p-4 mb-4">
            <h2 className="fw-semibold fs-5 mb-4">Branding</h2>

            {/* Company contact details */}
            <div className="mb-4">
                <div className="text-muted small fw-semibold mb-2">
                    Company contact details
                    <span className="fw-normal ms-1">(shown on quote PDFs)</span>
                    <SaveIndicator />
                </div>
                <div className="row g-2">
                    <div className="col-12">
                        <input className="form-control form-control-sm" placeholder="Address line 1"
                               value={currentContact.addressLine1}
                               onChange={e => handleContactChange('addressLine1', e.target.value)} />
                    </div>
                    <div className="col-12">
                        <input className="form-control form-control-sm" placeholder="Address line 2"
                               value={currentContact.addressLine2}
                               onChange={e => handleContactChange('addressLine2', e.target.value)} />
                    </div>
                    <div className="col-8">
                        <input className="form-control form-control-sm" placeholder="Town / City"
                               value={currentContact.townCity}
                               onChange={e => handleContactChange('townCity', e.target.value)} />
                    </div>
                    <div className="col-4">
                        <input className="form-control form-control-sm" placeholder="Postcode"
                               value={currentContact.postcode}
                               onChange={e => handleContactChange('postcode', e.target.value)} />
                    </div>
                    <div className="col-sm-4">
                        <input className="form-control form-control-sm" placeholder="Phone" type="tel"
                               value={currentContact.phone}
                               onChange={e => handleContactChange('phone', e.target.value)} />
                    </div>
                    <div className="col-sm-4">
                        <input className="form-control form-control-sm" placeholder="Email" type="email"
                               value={currentContact.email}
                               onChange={e => handleContactChange('email', e.target.value)} />
                    </div>
                    <div className="col-sm-4">
                        <input className="form-control form-control-sm" placeholder="Website"
                               value={currentContact.website}
                               onChange={e => handleContactChange('website', e.target.value)} />
                    </div>
                </div>
            </div>

            {/* Brand colour */}
            <div className="mb-4">
                <div className="text-muted small mb-2">
                    Brand colour
                    <SaveIndicator />
                </div>
                <div className="d-flex align-items-center gap-3">
                    <input type="color" className="form-control form-control-color"
                           style={{ width: 48, height: 38, padding: 2, cursor: 'pointer' }}
                           value={currentColour} onChange={handleColourChange}
                           title="Pick brand colour" />
                    <span className="text-muted small font-monospace">{currentColour}</span>
                </div>
                <div className="text-muted small mt-1">Used as the accent colour on quote PDFs.</div>
            </div>

            {/* Template picker */}
            <div className="mb-4">
                <div className="text-muted small mb-3">Quote template</div>
                <div className="d-flex flex-wrap gap-3">
                    {TEMPLATES.map(({ id, label, Thumb, description }) => {
                        const selected = selectedTemplate === id;
                        return (
                            <button key={id} onClick={() => handleSelectTemplate(id)}
                                    disabled={templateSaving}
                                    className="border-0 bg-transparent p-0 text-start"
                                    style={{ outline: 'none' }}>
                                <div className="rounded overflow-hidden"
                                     style={{
                                         border: selected
                                             ? `2.5px solid ${currentColour}`
                                             : '2px solid var(--bs-border-color)',
                                         transition: 'border-color 0.15s',
                                         cursor: 'pointer', background: 'white', width: 94,
                                     }}>
                                    <Thumb colour={currentColour} />
                                </div>
                                <div className="mt-1 text-center" style={{ width: 94 }}>
                                    <div className="small fw-semibold" style={{ fontSize: '0.78rem' }}>
                                        {label}
                                        {selected && <span style={{ color: currentColour }}> ✓</span>}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {TEMPLATES.find(t => t.id === selectedTemplate) && (
                    <div className="text-muted small mt-2">
                        {TEMPLATES.find(t => t.id === selectedTemplate).description}
                    </div>
                )}
            </div>

            {/* Signature toggle */}
            <div>
                <div className="text-muted small fw-semibold mb-2">Quote exports</div>
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch"
                           id="includeSignatureToggle"
                           checked={currentSignature}
                           onChange={e => handleToggleSignature(e.target.checked)}
                           disabled={updateBranding.isPending} />
                    <label className="form-check-label" htmlFor="includeSignatureToggle">
                        Include signature area on exported quotes
                    </label>
                </div>
                <div className="text-muted small mt-1">
                    Adds a signature block at the bottom of the PDF for client acceptance.
                </div>
            </div>
        </div>
    );
}

// ── Category list section ─────────────────────────────────────────────────────

function CategorySection({ targetType, title }) {
    const { data: categories = [], isLoading, error } = useAdminCategories(targetType);
    const createCategory = useCreateCategory();
    const updateCategory = useUpdateCategory();
    const toggleActive   = useToggleCategoryActive();
    const reorder        = useReorderCategories();

    const [newName, setNewName]           = useState('');
    const [editingId, setEditingId]       = useState(null);
    const [editingName, setEditingName]   = useState('');
    const [createError, setCreateError]   = useState(null);
    const [editError, setEditError]       = useState(null);

    if (isLoading) return <div className="d-flex justify-content-center py-4"><Spinner /></div>;
    if (error)     return <div className="text-danger">Failed to load {title.toLowerCase()}.</div>;

    function startEdit(cat) {
        setEditingId(cat.id);
        setEditingName(cat.name);
        setEditError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingName('');
        setEditError(null);
    }

    async function saveEdit(cat) {
        if (!editingName.trim()) return;
        try {
            await updateCategory.mutateAsync({ id: cat.id, name: editingName.trim(), targetType });
            setEditingId(null);
        } catch (err) {
            setEditError(err.message);
        }
    }

    async function handleCreate(e) {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setCreateError(null);
        try {
            await createCategory.mutateAsync({ name, targetType });
            setNewName('');
        } catch (err) {
            setCreateError(err.message);
        }
    }

    function move(index, direction) {
        const ordered = [...categories];
        const target  = index + direction;
        if (target < 0 || target >= ordered.length) return;
        [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
        reorder.mutate({ targetType, orderedIds: ordered.map(c => c.id) });
    }

    const active   = categories.filter(c => c.isActive);
    const inactive = categories.filter(c => !c.isActive);

    return (
        <div>
            <p className="text-muted small mb-3">
                {active.length} active · {inactive.length} inactive
            </p>

            {/* Active categories */}
            {active.length > 0 && (
                <div className="mb-3">
                    {active.map((cat, index) => (
                        <div key={cat.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)' }}>

                            {/* Reorder arrows */}
                            <div className="d-flex flex-column flex-shrink-0" style={{ gap: 1 }}>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, -1)}
                                        disabled={index === 0 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move up">
                                    <BiChevronUp />
                                </button>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, 1)}
                                        disabled={index === active.length - 1 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move down">
                                    <BiChevronDown />
                                </button>
                            </div>

                            {/* Name — inline edit on click */}
                            {editingId === cat.id ? (
                                <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <Input bsSize="sm" value={editingName}
                                           onChange={e => setEditingName(e.target.value)}
                                           onKeyDown={e => {
                                               if (e.key === 'Enter')  saveEdit(cat);
                                               if (e.key === 'Escape') cancelEdit();
                                           }}
                                           autoFocus />
                                    <Button size="sm" color="primary" onClick={() => saveEdit(cat)}
                                            disabled={updateCategory.isPending} className="px-2">
                                        <BiCheck />
                                    </Button>
                                    <Button size="sm" color="secondary" onClick={cancelEdit} className="px-2">
                                        ✕
                                    </Button>
                                    {editError && <span className="text-danger small">{editError}</span>}
                                </div>
                            ) : (
                                <span className="flex-grow-1 fw-medium"
                                      role="button" onClick={() => startEdit(cat)}
                                      title="Click to rename" style={{ cursor: 'text' }}>
                                    {cat.name}
                                </span>
                            )}

                            {/* Deactivate button */}
                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate({ id: cat.id, targetType })}
                                    disabled={toggleActive.isPending}
                                    title="Deactivate (hide from dropdowns)">
                                <BiHide size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add new */}
            <form onSubmit={handleCreate} className="d-flex align-items-center gap-2 mb-3">
                <Input bsSize="sm" placeholder={`Add a ${title.toLowerCase().replace(' categories', '')} category…`}
                       value={newName} onChange={e => setNewName(e.target.value)} />
                <Button type="submit" color="primary" size="sm"
                        disabled={createCategory.isPending || !newName.trim()}
                        className="d-inline-flex align-items-center gap-1 text-nowrap">
                    {createCategory.isPending ? <Spinner size="sm" /> : <BiPlus />}
                    Add
                </Button>
            </form>
            {createError && <div className="text-danger small mb-2">{createError}</div>}

            {/* Inactive categories (collapsed section) */}
            {inactive.length > 0 && (
                <div className="border-top pt-3 mt-1">
                    <div className="text-muted small mb-2 fw-semibold">Inactive</div>
                    {inactive.map(cat => (
                        <div key={cat.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)', opacity: 0.55 }}>
                            <span className="flex-grow-1 text-muted fst-italic small">{cat.name}</span>
                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate({ id: cat.id, targetType })}
                                    disabled={toggleActive.isPending}
                                    title="Reactivate">
                                <BiShow size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {categories.length === 0 && (
                <p className="text-muted small">No categories yet. Add one above.</p>
            )}
        </div>
    );
}

// ── Members section ──────────────────────────────────────────────────────────

// Role helpers — bridges Clerk's two-role world (org:admin / org:member +
// publicMetadata.workerMode) to MarginPal's three-role world (Admin /
// Manager / Worker). Single source of truth for the mapping in the UI;
// API mirrors this in TenantMiddleware.ComputeRole.
const MEMBERSHIP_TO_ROLE = (m) => {
    if (m?.role === 'org:admin') return 'Admin';
    if (m?.publicMetadata?.workerMode === true) return 'Worker';
    return 'Manager';
};
const ROLE_TO_CLERK = (role) => {
    if (role === 'Admin')  return { role: 'org:admin',  publicMetadata: {} };
    if (role === 'Worker') return { role: 'org:member', publicMetadata: { workerMode: true } };
    return                       { role: 'org:member', publicMetadata: { workerMode: false } };
};
// MarginPal role → API role string (used by CheckInviteEligibility).
const ROLE_TO_API = (role) => role === 'Manager' ? 'Member' : role;

const ROLE_DESCRIPTIONS = {
    Admin:   'Full access including billing, settings and team management.',
    Manager: 'Day-to-day running of customers, jobs, quotes, invoices and expenses. Cannot change billing or settings.',
    Worker:  "Logs receipts on the jobs they're assigned to. Cannot see revenue, profit or other commercial figures.",
};

function MembersSection() {
    const { organization } = useOrganization();
    const { user } = useUser();
    const { maxMembers, isLoading: tierLoading } = useTierFeatures();

    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('Manager');
    const [inviting, setInviting] = useState(false);
    const [error, setError] = useState(null);
    // Confirmation dialog state. `target` holds the membership/invitation
    // we're about to act on so the dialog can show its label, and so the
    // confirm handler knows which row to act on.
    const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
    const [removingMember, setRemovingMember] = useState(false);
    const [revokeInviteTarget, setRevokeInviteTarget] = useState(null);
    const [revokingInvite, setRevokingInvite] = useState(false);

    const refresh = async () => {
        if (!organization) return;
        try {
            const [memberList, inviteList] = await Promise.all([
                organization.getMemberships({ limit: 100 }),
                organization.getInvitations({ status: ['pending'] }),
            ]);
            setMembers(memberList.data ?? []);
            setInvitations(inviteList.data ?? []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    useEffect(() => { refresh(); }, [organization?.id]);

    const totalSeats = members.length + invitations.length;
    const atLimit = maxMembers != null && totalSeats >= maxMembers;

    async function handleInvite(e) {
        e.preventDefault();
        const addr = email.trim();
        if (!addr) return;
        setError(null);
        setInviting(true);
        try {
            // Pre-flight the multi-org Admin/Manager rule. Workers always
            // pass; Admin/Manager can fail with a friendly explanation
            // referencing the conflicting org.
            const eligibility = await checkInviteEligibility(addr, ROLE_TO_API(inviteRole));
            if (!eligibility.allowed) {
                setError(eligibility.reason || 'This email is not eligible for the chosen role.');
                setInviting(false);
                return;
            }

            // Route through our API rather than the Clerk JS SDK — the
            // SDK's inviteMembers silently drops publicMetadata, which is
            // how we encode Worker (org:member + workerMode flag). Without
            // metadata on the invitation, the membership-accept webhook
            // can't see workerMode and treats the new Worker as a Member,
            // tripping the multi-org backstop and revoking the membership.
            await inviteMember(addr, ROLE_TO_API(inviteRole));
            setEmail('');
            await refresh();
        } catch (err) {
            setError(err?.errors?.[0]?.longMessage || err?.message || 'Failed to invite member');
        } finally {
            setInviting(false);
        }
    }

    function handleRemove(membership) {
        setRemoveMemberTarget(membership);
    }

    async function performRemoveMember() {
        if (!removeMemberTarget) return;
        setRemovingMember(true);
        try {
            await removeMemberTarget.destroy();
            await refresh();
            setRemoveMemberTarget(null);
        } catch (err) {
            setError(err?.message || 'Failed to remove member.');
        } finally {
            setRemovingMember(false);
        }
    }

    async function handleToggleRole(membership) {
        const newRole = membership.role === 'org:admin' ? 'org:member' : 'org:admin';
        try { await membership.update({ role: newRole }); await refresh(); }
        catch (err) { setError(err?.message || 'Failed to change role.'); }
    }

    function handleUninvite(invitation) {
        setRevokeInviteTarget(invitation);
    }

    async function performRevokeInvite() {
        if (!revokeInviteTarget) return;
        setRevokingInvite(true);
        try {
            await revokeInviteTarget.revoke();
            await refresh();
            setRevokeInviteTarget(null);
        } catch (err) {
            setError(err?.message || 'Failed to revoke invitation.');
        } finally {
            setRevokingInvite(false);
        }
    }

    if (loading || tierLoading) {
        return (
            <div className="surface p-4 mb-4">
                <h2 className="fw-semibold fs-5 mb-3">Members</h2>
                <Spinner size="sm" />
            </div>
        );
    }

    return (
        <div className="surface p-4 mb-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                    <h2 className="fw-semibold fs-5 mb-1">Members</h2>
                    <p className="text-muted small mb-0">
                        {members.length} member{members.length !== 1 ? 's' : ''}
                        {invitations.length > 0 && `, ${invitations.length} pending`}
                        {maxMembers != null && ` (limit: ${maxMembers})`}
                    </p>
                </div>
            </div>

            {/* Current members */}
            {members.map(m => {
                const isCurrentUser = m.publicUserData?.userId === user?.id;
                const currentRole = MEMBERSHIP_TO_ROLE(m);
                return (
                    <div key={m.id}
                         className="d-flex align-items-center gap-2 rounded px-3 py-2 mb-1"
                         style={{ background: 'var(--bs-tertiary-bg)' }}>
                        <BiUser className="text-muted flex-shrink-0" />
                        <div className="flex-grow-1">
                            <span className="fw-medium">
                                {m.publicUserData?.firstName || m.publicUserData?.identifier || 'Member'}
                                {m.publicUserData?.lastName ? ` ${m.publicUserData.lastName}` : ''}
                            </span>
                            <div className="text-muted small">{m.publicUserData?.identifier}</div>
                        </div>
                        <Input type="select" bsSize="sm"
                            value={currentRole}
                            onChange={async (e) => {
                                const nextRole = e.target.value;
                                if (nextRole === currentRole) return;
                                // Pre-flight the multi-org rule before
                                // attempting the update. Surfaces "this user
                                // is already a Manager elsewhere" before we
                                // touch Clerk.
                                try {
                                    if (nextRole !== 'Worker' && m.publicUserData?.identifier) {
                                        const elig = await checkInviteEligibility(
                                            m.publicUserData.identifier, ROLE_TO_API(nextRole));
                                        if (!elig.allowed) {
                                            setError(elig.reason || 'Cannot change role.');
                                            return;
                                        }
                                    }
                                    const payload = ROLE_TO_CLERK(nextRole);
                                    await m.update({
                                        role: payload.role,
                                        publicMetadata: payload.publicMetadata,
                                    });
                                    await refresh();
                                }
                                catch (err) { setError(err?.message || 'Failed to change role.'); }
                            }}
                            disabled={isCurrentUser}
                            style={{ width: 'auto', fontSize: '0.8rem' }}
                        >
                            <option value="Admin">Admin</option>
                            <option value="Manager">Manager</option>
                            <option value="Worker">Worker</option>
                        </Input>
                        {!isCurrentUser && (
                            <button className="btn btn-link p-0 text-danger flex-shrink-0"
                                    onClick={() => handleRemove(m)} title="Remove member">
                                <BiTrash size={16} />
                            </button>
                        )}
                    </div>
                );
            })}

            {/* Pending invitations */}
            {invitations.length > 0 && (
                <div className="mt-2">
                    <div className="text-muted small fw-semibold mb-1">Pending Invitations</div>
                    {invitations.map(inv => (
                        <div key={inv.id}
                             className="d-flex align-items-center gap-2 rounded px-3 py-2 mb-1"
                             style={{ background: 'var(--bs-tertiary-bg)', opacity: 0.7 }}>
                            <BiEnvelope className="text-muted flex-shrink-0" />
                            <span className="flex-grow-1 small">{inv.emailAddress}</span>
                            <span className="text-warning small fw-semibold">Pending</span>
                            <button className="btn btn-link p-0 text-danger flex-shrink-0"
                                    onClick={() => handleUninvite(inv)} title="Revoke invitation">
                                <BiTrash size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Invite form */}
            {atLimit ? (
                <div className="text-danger small mt-3">
                    Member limit reached ({totalSeats}/{maxMembers}). Upgrade your plan to add more.
                </div>
            ) : (
                <>
                    <form onSubmit={handleInvite} className="d-flex align-items-center gap-2 mt-3">
                        <Input bsSize="sm" type="email" placeholder="Email address"
                               value={email} onChange={e => setEmail(e.target.value)} />
                        <Button type="submit" color="primary" size="sm"
                                disabled={inviting || !email.trim()}
                                className="d-inline-flex align-items-center gap-1 text-nowrap">
                            {inviting ? <Spinner size="sm" /> : <BiPlus />}
                            Invite
                        </Button>
                    </form>
                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                        <span className="text-muted small">Invite as:</span>
                        {['Admin', 'Manager', 'Worker'].map(role => (
                            <button key={role}
                                    type="button"
                                    onClick={() => setInviteRole(role)}
                                    className={`btn btn-sm ${inviteRole === role ? 'btn-primary' : 'btn-outline-secondary'}`}>
                                {role}
                            </button>
                        ))}
                    </div>
                    <div className="text-muted small mt-2 fst-italic">
                        {ROLE_DESCRIPTIONS[inviteRole]}
                    </div>
                </>
            )}
            {error && <div className="text-danger small mt-2">{error}</div>}

            <DialogBox
                isOpen={!!removeMemberTarget}
                toggle={() => !removingMember && setRemoveMemberTarget(null)}
                title="Remove member?"
                message={`${removeMemberTarget?.publicUserData?.identifier ?? 'This member'} will lose access to your organisation.`}
                buttons={[
                    { label: 'Cancel', color: 'secondary', onClick: () => setRemoveMemberTarget(null), disabled: removingMember },
                    { label: 'Remove', color: 'danger', onClick: performRemoveMember, loading: removingMember },
                ]}
            />

            <DialogBox
                isOpen={!!revokeInviteTarget}
                toggle={() => !revokingInvite && setRevokeInviteTarget(null)}
                title="Revoke invitation?"
                message={`The invite to ${revokeInviteTarget?.emailAddress ?? 'this email'} will be cancelled. They can be re-invited later if needed.`}
                buttons={[
                    { label: 'Cancel', color: 'secondary', onClick: () => setRevokeInviteTarget(null), disabled: revokingInvite },
                    { label: 'Revoke', color: 'danger', onClick: performRevokeInvite, loading: revokingInvite },
                ]}
            />
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TIER_LABELS = { solo: 'Solo', pro: 'Pro', business: 'Business' };
const TIER_COLORS = { solo: '#10b981', pro: '#26ada9', business: '#8b5cf6' };

function BillingSection() {
    const { data: usage, isLoading, refetch } = useBillingUsage();
    const [redirecting, setRedirecting] = useState(false);
    const [cancellingPending, setCancellingPending] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);

    // Reset spinner and refresh data when returning from Stripe portal (bfcache).
    useEffect(() => {
        const handlePageShow = (e) => {
            if (e.persisted) {
                setRedirecting(false);
                refetch();
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [refetch]);

    const handleManage = async () => {
        setRedirecting(true);
        try {
            const { url } = await createPortalSession();
            window.location.href = url;
        } catch {
            setRedirecting(false);
        }
    };

    const [cancelPendingDialogOpen, setCancelPendingDialogOpen] = useState(false);

    const performCancelPending = async () => {
        if (!usage?.pendingTier) return;
        setCancellingPending(true);
        try {
            await cancelPendingTierChange();
            refetch();
            toast.success('Scheduled change cancelled.');
            setCancelPendingDialogOpen(false);
        } catch (err) {
            toast.error(err?.message ?? 'Couldn\'t cancel. Please try again.');
        } finally {
            setCancellingPending(false);
        }
    };

    const handleCancelPending = () => {
        if (!usage?.pendingTier) return;
        setCancelPendingDialogOpen(true);
    };

    if (isLoading) {
        return (
            <div className="surface p-4 mb-4">
                <h2 className="fw-semibold fs-5 mb-3">Subscription & Billing</h2>
                <Spinner size="sm" />
            </div>
        );
    }

    if (!usage || usage.status === 'none') return null;

    const usagePercent = usage.monthlyLimit > 0
        ? Math.min(100, Math.round((usage.scansUsed / usage.monthlyLimit) * 100))
        : 0;
    const atLimit = usage.monthlyLimit > 0 && usage.scansUsed >= usage.monthlyLimit;
    const periodEnd = usage.periodEnd
        ? new Date(usage.periodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;

    return (
        <div className="surface p-4 mb-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                <div>
                    <h2 className="fw-semibold fs-5 mb-1">Subscription & Billing</h2>
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="badge"
                            style={{ background: TIER_COLORS[usage.tier] || '#64748b', fontSize: '0.8rem' }}
                        >
                            {TIER_LABELS[usage.tier] || usage.tier}
                        </span>
                        <span className="text-muted small" style={{ textTransform: 'capitalize' }}>
                            {usage.status === 'trialing' ? 'Free trial' : usage.status}
                        </span>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        size="sm"
                        color="primary"
                        onClick={() => setPlanModalOpen(true)}
                    >
                        Change plan
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={handleManage}
                        disabled={redirecting}
                    >
                        {redirecting ? <Spinner size="sm" /> : 'Manage billing'}
                    </Button>
                </div>
            </div>

            {usage.pendingTier && (
                <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 py-2 px-3 mb-3">
                    <div className="small">
                        Switching to <strong>{TIER_LABELS[usage.pendingTier] || usage.pendingTier}</strong> on{' '}
                        {new Date(usage.pendingTierEffectiveAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <Button
                        size="sm"
                        color="link"
                        className="p-0 text-danger fw-semibold"
                        onClick={handleCancelPending}
                        disabled={cancellingPending}
                    >
                        {cancellingPending ? <Spinner size="sm" /> : 'Cancel'}
                    </Button>
                </div>
            )}

            <ChangePlanModal
                isOpen={planModalOpen}
                toggle={() => setPlanModalOpen(false)}
                currentTier={usage.tier}
                pendingTier={usage.pendingTier}
            />

            <DialogBox
                isOpen={cancelPendingDialogOpen}
                toggle={() => !cancellingPending && setCancelPendingDialogOpen(false)}
                title="Cancel scheduled change?"
                message={`You'll stay on ${TIER_LABELS[usage.tier] || usage.tier} indefinitely instead of switching to ${TIER_LABELS[usage.pendingTier] || usage.pendingTier} at renewal.`}
                buttons={[
                    {
                        label: 'Keep change',
                        color: 'secondary',
                        onClick: () => setCancelPendingDialogOpen(false),
                        disabled: cancellingPending,
                    },
                    {
                        label: 'Cancel change',
                        color: 'danger',
                        onClick: performCancelPending,
                        loading: cancellingPending,
                    },
                ]}
            />

            <div className="mb-2">
                <div className="d-flex justify-content-between small mb-1">
                    <span>Receipt scans this period</span>
                    <span className="fw-semibold">{usage.scansUsed} / {usage.monthlyLimit}</span>
                </div>
                <Progress
                    value={usagePercent}
                    color={usagePercent >= 100 ? 'danger' : usagePercent >= 90 ? 'warning' : 'primary'}
                    style={{ height: 8, borderRadius: 4, backgroundColor: '#d1d5db' }}
                />
            </div>

            {atLimit && (
                <p className="text-danger small mb-1">
                    You've reached your scan limit for this period. Upgrade your plan or wait for it to reset to scan more.
                </p>
            )}

            {periodEnd && (
                <p className="text-muted small mb-2">
                    Period resets {periodEnd}
                </p>
            )}

        </div>
    );
}

// ── Cost Type Section ─────────────────────────────────────────────────────
function CostTypeSection() {
    const { data: costTypes = [], isLoading, error } = useAdminCostTypes();
    const createMutation  = useCreateCostType();
    const updateMutation  = useUpdateCostType();
    const toggleActive    = useToggleCostTypeActive();
    const reorder         = useReorderCostTypes();

    const [newName, setNewName]         = useState('');
    const [editingId, setEditingId]     = useState(null);
    const [editingName, setEditingName] = useState('');
    const [createError, setCreateError] = useState(null);
    const [editError, setEditError]     = useState(null);

    if (isLoading) return <div className="d-flex justify-content-center py-4"><Spinner /></div>;
    if (error)     return <div className="text-danger">Failed to load cost types.</div>;

    function startEdit(item) {
        setEditingId(item.id);
        setEditingName(item.name);
        setEditError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingName('');
        setEditError(null);
    }

    async function saveEdit(item) {
        if (!editingName.trim()) return;
        try {
            await updateMutation.mutateAsync({ id: item.id, name: editingName.trim() });
            setEditingId(null);
        } catch (err) {
            setEditError(err.message);
        }
    }

    async function handleCreate(e) {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setCreateError(null);
        try {
            await createMutation.mutateAsync(name);
            setNewName('');
        } catch (err) {
            setCreateError(err.message);
        }
    }

    function move(index, direction) {
        const ordered = [...active];
        const target  = index + direction;
        if (target < 0 || target >= ordered.length) return;
        [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
        reorder.mutate(ordered.map(c => c.id));
    }

    const active   = costTypes.filter(c => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
    const inactive = costTypes.filter(c => !c.isActive);

    return (
        <div>
            <p className="text-muted small mb-3">
                {active.length} active · {inactive.length} inactive
            </p>

            {/* Active cost types */}
            {active.length > 0 && (
                <div className="mb-3">
                    {active.map((item, index) => (
                        <div key={item.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)' }}>

                            {/* Reorder arrows */}
                            <div className="d-flex flex-column flex-shrink-0" style={{ gap: 1 }}>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, -1)}
                                        disabled={index === 0 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move up">
                                    <BiChevronUp />
                                </button>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, 1)}
                                        disabled={index === active.length - 1 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move down">
                                    <BiChevronDown />
                                </button>
                            </div>

                            {/* Name — inline edit on click */}
                            {editingId === item.id ? (
                                <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <Input bsSize="sm" value={editingName}
                                           onChange={e => setEditingName(e.target.value)}
                                           onKeyDown={e => {
                                               if (e.key === 'Enter')  saveEdit(item);
                                               if (e.key === 'Escape') cancelEdit();
                                           }}
                                           autoFocus />
                                    <Button size="sm" color="primary" onClick={() => saveEdit(item)}
                                            disabled={updateMutation.isPending} className="px-2">
                                        <BiCheck />
                                    </Button>
                                    <Button size="sm" color="secondary" onClick={cancelEdit} className="px-2">
                                        ✕
                                    </Button>
                                    {editError && <span className="text-danger small">{editError}</span>}
                                </div>
                            ) : (
                                <span className="flex-grow-1 fw-medium"
                                      role="button" onClick={() => startEdit(item)}
                                      title="Click to rename" style={{ cursor: 'text' }}>
                                    {item.name}
                                </span>
                            )}

                            {/* Deactivate button */}
                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate(item.id)}
                                    disabled={toggleActive.isPending}
                                    title="Deactivate (hide from dropdowns)">
                                <BiHide size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add new */}
            <form onSubmit={handleCreate} className="d-flex align-items-center gap-2 mb-3">
                <Input bsSize="sm" placeholder="Add a cost type…"
                       value={newName} onChange={e => setNewName(e.target.value)} />
                <Button type="submit" color="primary" size="sm"
                        disabled={createMutation.isPending || !newName.trim()}
                        className="d-inline-flex align-items-center gap-1 text-nowrap">
                    {createMutation.isPending ? <Spinner size="sm" /> : <BiPlus />}
                    Add
                </Button>
            </form>
            {createError && <div className="text-danger small mb-2">{createError}</div>}

            {/* Inactive cost types */}
            {inactive.length > 0 && (
                <div className="border-top pt-3 mt-1">
                    <div className="text-muted small mb-2 fw-semibold">Inactive</div>
                    {inactive.map(item => (
                        <div key={item.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)', opacity: 0.55 }}>
                            <span className="flex-grow-1 text-muted fst-italic small">{item.name}</span>
                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate(item.id)}
                                    disabled={toggleActive.isPending}
                                    title="Reactivate">
                                <BiShow size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {costTypes.length === 0 && (
                <p className="text-muted small">No cost types yet. Add one above.</p>
            )}
        </div>
    );
}

// Job types — same shape as CostTypeSection. Tenants edit the high-level
// kind of work each job represents (Kitchen, Bathroom, Roofing, etc.).
function JobTypeSection() {
    const { data: jobTypes = [], isLoading, error } = useAdminJobTypes();
    const createMutation  = useCreateJobType();
    const updateMutation  = useUpdateJobType();
    const toggleActive    = useToggleJobTypeActive();
    const reorder         = useReorderJobTypes();

    const [newName, setNewName]         = useState('');
    const [editingId, setEditingId]     = useState(null);
    const [editingName, setEditingName] = useState('');
    const [createError, setCreateError] = useState(null);
    const [editError, setEditError]     = useState(null);

    if (isLoading) return <div className="d-flex justify-content-center py-4"><Spinner /></div>;
    if (error)     return <div className="text-danger">Failed to load job types.</div>;

    function startEdit(item) {
        setEditingId(item.id);
        setEditingName(item.name);
        setEditError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingName('');
        setEditError(null);
    }

    async function saveEdit(item) {
        if (!editingName.trim()) return;
        try {
            await updateMutation.mutateAsync({ id: item.id, name: editingName.trim() });
            setEditingId(null);
        } catch (err) {
            setEditError(err.message);
        }
    }

    async function handleCreate(e) {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setCreateError(null);
        try {
            await createMutation.mutateAsync(name);
            setNewName('');
        } catch (err) {
            setCreateError(err.message);
        }
    }

    function move(index, direction) {
        const ordered = [...active];
        const target  = index + direction;
        if (target < 0 || target >= ordered.length) return;
        [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
        reorder.mutate(ordered.map(j => j.id));
    }

    const active   = jobTypes.filter(j => j.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
    const inactive = jobTypes.filter(j => !j.isActive);

    return (
        <div>
            <p className="text-muted small mb-3">
                {active.length} active · {inactive.length} inactive
            </p>

            {active.length > 0 && (
                <div className="mb-3">
                    {active.map((item, index) => (
                        <div key={item.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)' }}>

                            <div className="d-flex flex-column flex-shrink-0" style={{ gap: 1 }}>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, -1)}
                                        disabled={index === 0 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move up">
                                    <BiChevronUp />
                                </button>
                                <button className="btn btn-link p-0 lh-1 text-muted"
                                        onClick={() => move(index, 1)}
                                        disabled={index === active.length - 1 || reorder.isPending}
                                        style={{ fontSize: 14 }} title="Move down">
                                    <BiChevronDown />
                                </button>
                            </div>

                            {editingId === item.id ? (
                                <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <Input bsSize="sm" value={editingName}
                                           onChange={e => setEditingName(e.target.value)}
                                           onKeyDown={e => {
                                               if (e.key === 'Enter')  saveEdit(item);
                                               if (e.key === 'Escape') cancelEdit();
                                           }}
                                           autoFocus />
                                    <Button size="sm" color="primary" onClick={() => saveEdit(item)}
                                            disabled={updateMutation.isPending} className="px-2">
                                        <BiCheck />
                                    </Button>
                                    <Button size="sm" color="secondary" onClick={cancelEdit} className="px-2">
                                        ✕
                                    </Button>
                                    {editError && <span className="text-danger small">{editError}</span>}
                                </div>
                            ) : (
                                <span className="flex-grow-1 fw-medium"
                                      role="button" onClick={() => startEdit(item)}
                                      title="Click to rename" style={{ cursor: 'text' }}>
                                    {item.name}
                                </span>
                            )}

                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate(item.id)}
                                    disabled={toggleActive.isPending}
                                    title="Deactivate (hide from dropdowns)">
                                <BiHide size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <form onSubmit={handleCreate} className="d-flex align-items-center gap-2 mb-3">
                <Input bsSize="sm" placeholder="Add a job type…"
                       value={newName} onChange={e => setNewName(e.target.value)} />
                <Button type="submit" color="primary" size="sm"
                        disabled={createMutation.isPending || !newName.trim()}
                        className="d-inline-flex align-items-center gap-1 text-nowrap">
                    {createMutation.isPending ? <Spinner size="sm" /> : <BiPlus />}
                    Add
                </Button>
            </form>
            {createError && <div className="text-danger small mb-2">{createError}</div>}

            {inactive.length > 0 && (
                <div className="border-top pt-3 mt-1">
                    <div className="text-muted small mb-2 fw-semibold">Inactive</div>
                    {inactive.map(item => (
                        <div key={item.id}
                             className="d-flex align-items-center gap-2 px-2 py-2"
                             style={{ borderBottom: '1px solid var(--bs-border-color-translucent)', opacity: 0.55 }}>
                            <span className="flex-grow-1 text-muted fst-italic small">{item.name}</span>
                            <button className="btn btn-link p-0 text-muted flex-shrink-0"
                                    onClick={() => toggleActive.mutate(item.id)}
                                    disabled={toggleActive.isPending}
                                    title="Reactivate">
                                <BiShow size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {jobTypes.length === 0 && (
                <p className="text-muted small">No job types yet. Add one above.</p>
            )}
        </div>
    );
}

// Wraps the four editable lookup-table sections in one card with a
// left-side sub-nav. Replaces what used to be four standalone surface
// cards on the settings page. Same layout pattern as the Help screen.
const CUSTOMISATIONS_TABS = [
    { id: 'jobTypes',         label: 'Job Types' },
    { id: 'costTypes',        label: 'Quote Line Item Types' },
    { id: 'expenseCategories', label: 'Expense Categories' },
    { id: 'invoiceCategories', label: 'Invoice Categories' },
];

function CustomisationsSection() {
    const [active, setActive] = useState('jobTypes');
    const selected = CUSTOMISATIONS_TABS.find(t => t.id === active) || CUSTOMISATIONS_TABS[0];

    // Pre-fetch all four lookup tables on mount so the React Query cache is
    // warm before the user starts switching tabs. Without this, the first
    // visit to each tab fires its query → spinner → content → page jumps.
    // With it, subsequent tab switches render instantly from cache.
    useAdminJobTypes();
    useAdminCostTypes();
    useAdminCategories(CATEGORY_TARGET.Expense);
    useAdminCategories(CATEGORY_TARGET.Invoice);

    return (
        <div className="surface p-4 mb-4">
            <h2 className="fw-semibold fs-5 mb-1">Customisations</h2>
            <p className="text-muted small mb-3">
                Tailor the lookup lists used across jobs, quotes, expenses and invoices.
            </p>

            <div className="row g-3">
                <aside className="col-12 col-md-4 col-lg-3">
                    <Nav vertical pills className="customisations-nav">
                        {CUSTOMISATIONS_TABS.map(t => (
                            <NavItem key={t.id}>
                                <button
                                    type="button"
                                    className={`nav-link text-start w-100${t.id === selected.id ? ' active' : ''}`}
                                    onClick={() => setActive(t.id)}
                                >
                                    {t.label}
                                </button>
                            </NavItem>
                        ))}
                    </Nav>
                </aside>
                {/* Min-height stops the page from collapsing onto the
                    loading spinner the very first time a tab is visited
                    before its data lands. */}
                <section className="col-12 col-md-8 col-lg-9" style={{ minHeight: 480 }}>
                    {selected.id === 'jobTypes'         && <JobTypeSection />}
                    {selected.id === 'costTypes'        && <CostTypeSection />}
                    {selected.id === 'expenseCategories' && <CategorySection targetType={CATEGORY_TARGET.Expense} title="Expense Categories" />}
                    {selected.id === 'invoiceCategories' && <CategorySection targetType={CATEGORY_TARGET.Invoice} title="Invoice Categories" />}
                </section>
            </div>
        </div>
    );
}

function OrganisationSection() {
    const { organization } = useOrganization();
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const [deleteLogoDialogOpen, setDeleteLogoDialogOpen] = useState(false);
    const [deletingLogo, setDeletingLogo] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (organization?.name) setName(organization.name);
    }, [organization?.name]);

    const handleSaveName = async () => {
        if (!name.trim() || name.trim() === organization?.name) return;
        setSaving(true);
        try {
            await organization.update({ name: name.trim() });
            // Mirror the new name into our Company.Name. PDFs, exports, and
            // the dashboard title all read from there, not from Clerk.
            try { await syncName(name.trim()); } catch { /* best-effort */ }
        }
        catch { /* ignore */ }
        finally { setSaving(false); }
    };

    const handleUploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            const updated = await organization.setLogo({ file });
            try { await syncLogo(updated?.imageUrl || organization.imageUrl); } catch { /* best-effort */ }
        }
        catch { /* ignore */ }
        finally { setLogoUploading(false); e.target.value = ''; }
    };

    const handleDeleteLogo = () => {
        setDeleteLogoDialogOpen(true);
    };

    const performDeleteLogo = async () => {
        setDeletingLogo(true);
        try {
            await organization.deleteLogo();
            try { await syncLogo(null); } catch { /* best-effort */ }
            setDeleteLogoDialogOpen(false);
        } catch { /* ignore */ }
        finally { setDeletingLogo(false); }
    };



    return (
        <div className="surface p-4 mb-4">
            <h2 className="fw-semibold fs-5 mb-3">Organisation</h2>

            {/* Logo */}
            <div className="d-flex align-items-center gap-3 mb-3">
                {organization?.imageUrl ? (
                    <img src={organization.imageUrl} alt="Logo" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} />
                ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bs-tertiary-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BiUpload size={20} className="text-muted" />
                    </div>
                )}
                <div className="d-flex flex-column gap-1">
                    <div className="d-flex gap-2">
                        <Button size="sm" color="primary" outline onClick={() => fileRef.current?.click()} disabled={logoUploading}>
                            {logoUploading ? <Spinner size="sm" /> : (organization?.imageUrl ? 'Change' : 'Upload')}
                        </Button>
                        {organization?.imageUrl && (
                            <Button size="sm" color="danger" outline onClick={handleDeleteLogo}>Remove</Button>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="d-none" onChange={handleUploadLogo} />
                </div>
            </div>

            {/* Name */}
            <div className="d-flex align-items-end gap-2 mb-4">
                <div className="flex-grow-1">
                    <label className="form-label small fw-semibold">Organisation Name</label>
                    <Input bsSize="sm" value={name} onChange={e => setName(e.target.value)}
                           onBlur={handleSaveName} onKeyDown={e => e.key === 'Enter' && handleSaveName()} />
                </div>
                <Button size="sm" color="primary" onClick={handleSaveName} disabled={saving || !name.trim() || name.trim() === organization?.name}>
                    {saving ? <Spinner size="sm" /> : 'Save'}
                </Button>
            </div>

            <DialogBox
                isOpen={deleteLogoDialogOpen}
                toggle={() => !deletingLogo && setDeleteLogoDialogOpen(false)}
                title="Remove logo?"
                message="The organisation will use the default placeholder until a new logo is uploaded."
                buttons={[
                    { label: 'Cancel', color: 'secondary', onClick: () => setDeleteLogoDialogOpen(false), disabled: deletingLogo },
                    { label: 'Remove', color: 'danger', onClick: performDeleteLogo, loading: deletingLogo },
                ]}
            />
        </div>
    );
}

function DeleteOrganisationButton() {
    const { organization } = useOrganization();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const performDelete = async () => {
        setDeleting(true);
        try {
            await apiFetch(`${API_URL}/api/Billing/CancelSubscription`, { method: 'POST' }).catch(() => {});
            await organization.destroy();
            window.location.href = '/';
        } catch { /* ignore */ }
        finally { setDeleting(false); }
    };

    return (
        <>
            <Button color="danger" outline onClick={() => setDialogOpen(true)}
                    className="d-inline-flex align-items-center gap-2">
                <BiTrash size={18} /> Delete Organisation
            </Button>
            <DialogBox
                isOpen={dialogOpen}
                toggle={() => !deleting && setDialogOpen(false)}
                title="Delete organisation?"
                message="This is permanent. All your customers, jobs, quotes, expenses, invoices, and team members will be removed. This cannot be undone."
                buttons={[
                    { label: 'Cancel', color: 'secondary', onClick: () => setDialogOpen(false), disabled: deleting },
                    { label: 'Delete organisation', color: 'danger', onClick: performDelete, loading: deleting },
                ]}
            />
        </>
    );
}

// ── Base currency section ─────────────────────────────────────────────────────
// The company-wide reporting currency the dashboard rolls every job up into.
// Invoices/expenses keep their own currencies; this only changes what the
// dashboard KPIs and charts are expressed in.
function BaseCurrencySection() {
    const [value, setValue]     = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);

    useEffect(() => {
        let active = true;
        fetchBaseCurrency()
            .then(d => { if (active) setValue(d?.baseCurrency || DEFAULT_CURRENCY); })
            .catch(() => { if (active) setValue(DEFAULT_CURRENCY); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    async function handleSelect(code) {
        if (code === value || saving) return;
        const prev = value;
        setValue(code);          // optimistic
        setSaving(true);
        try {
            await updateBaseCurrency(code);
            toast.success(`Dashboard currency set to ${code}.`);
        } catch (err) {
            setValue(prev);      // revert on failure
            toast.error(err?.message || 'Failed to update dashboard currency.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="surface p-4 mb-4">
            <h2 className="fw-semibold fs-5 mb-1">Dashboard currency</h2>
            <p className="text-muted small mb-3">
                Dashboard totals are shown in this currency. Each invoice and expense keeps
                its own currency and is converted here at its transaction-date rate.
            </p>
            {loading ? (
                <Spinner size="sm" />
            ) : (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {CURRENCIES.map(c => (
                        <button
                            key={c.code}
                            type="button"
                            onClick={() => handleSelect(c.code)}
                            disabled={saving}
                            className={`btn btn-sm ${value === c.code ? 'btn-primary' : 'btn-outline-secondary'}`}
                        >
                            {c.symbol} {c.code}
                        </button>
                    ))}
                    {saving && <Spinner size="sm" className="ms-1" />}
                </div>
            )}
        </div>
    );
}

export default function SettingsPage() {
    const isAdmin = useIsOrgAdmin();

    // Still loading Clerk — show nothing to avoid flash
    if (isAdmin === undefined) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    // Non-admin navigated here directly — show locked state
    if (!isAdmin) {
        return (
            <div className="surface text-center py-5">
                <BiLock size={32} className="text-muted mb-3" />
                <h4 className="mb-2">Access restricted</h4>
                <p className="text-muted mb-0">
                    Only organisation admins can manage settings.
                </p>
            </div>
        );
    }

    return (
        <div>
            <h1 className="fw-bold mb-4">Settings</h1>
            <OrganisationSection />
            <BillingSection />
            <MembersSection />
            <BrandingSection />
            <BaseCurrencySection />
            <IntegrationsSection />
            <CustomisationsSection />
            <DeleteOrganisationButton />
        </div>
    );
}
